/**
 * useAnimatedMount Hook
 *
 * Handles delayed unmounting for CSS exit animations.
 * Returns mounting state and animation state for coordinating
 * enter/exit transitions.
 *
 * Usage:
 *   const { shouldRender, animationState } = useAnimatedMount(isOpen, 150);
 *   if (!shouldRender) return null;
 *   return <div className={animationState === 'entering' ? 'animate-in' : 'animate-out'} />
 */

import { useState, useEffect } from 'react';

export type AnimationState = 'entering' | 'entered' | 'exiting' | 'exited';

interface UseAnimatedMountOptions {
  /** Duration of the exit animation in ms (default: 150) */
  exitDuration?: number;
  /** Duration of the enter animation in ms (default: 150) */
  enterDuration?: number;
  /** Callback when exit animation completes */
  onExitComplete?: () => void;
}

interface UseAnimatedMountReturn {
  /** Whether the component should be rendered in the DOM */
  shouldRender: boolean;
  /** Current animation state */
  animationState: AnimationState;
  /** Whether currently animating (entering or exiting) */
  isAnimating: boolean;
  /** Whether visible (entering or entered) */
  isVisible: boolean;
}

export function useAnimatedMount(
  isOpen: boolean,
  options: UseAnimatedMountOptions = {}
): UseAnimatedMountReturn {
  const { exitDuration = 150, enterDuration = 150, onExitComplete } = options;

  const [shouldRender, setShouldRender] = useState(isOpen);
  const [animationState, setAnimationState] = useState<AnimationState>(
    isOpen ? 'entered' : 'exited'
  );

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    if (isOpen) {
      // Opening: render immediately, then trigger enter animation
      setShouldRender(true);
      // Use requestAnimationFrame to ensure DOM is ready before animating
      requestAnimationFrame(() => {
        setAnimationState('entering');
        timeoutId = setTimeout(() => {
          setAnimationState('entered');
        }, enterDuration);
      });
    } else if (shouldRender) {
      // Closing: trigger exit animation, then unmount
      setAnimationState('exiting');
      timeoutId = setTimeout(() => {
        setAnimationState('exited');
        setShouldRender(false);
        onExitComplete?.();
      }, exitDuration);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isOpen, exitDuration, enterDuration, onExitComplete, shouldRender]);

  return {
    shouldRender,
    animationState,
    isAnimating: animationState === 'entering' || animationState === 'exiting',
    isVisible: animationState === 'entering' || animationState === 'entered',
  };
}

/**
 * Animation duration constants (in ms)
 * Aligned with design system: minimal and purposeful
 */
export const ANIMATION_DURATION = {
  fast: 100,
  normal: 150,
  slow: 200,
} as const;

/**
 * CSS class helpers for common animation patterns
 */
export const modalAnimationClasses = {
  backdrop: {
    base: 'transition-opacity',
    entering: 'opacity-100',
    exiting: 'opacity-0',
    duration: 'duration-150',
  },
  content: {
    base: 'transition-all',
    entering: 'opacity-100 scale-100 translate-y-0',
    exiting: 'opacity-0 scale-[0.98] translate-y-1',
    duration: 'duration-150',
  },
  slideDown: {
    base: 'transition-all',
    entering: 'opacity-100 translate-y-0',
    exiting: 'opacity-0 -translate-y-full',
    duration: 'duration-200',
  },
  fadeScale: {
    base: 'transition-all',
    entering: 'opacity-100 scale-100',
    exiting: 'opacity-0 scale-95',
    duration: 'duration-150',
  },
} as const;
